<div class="page-container">
  <app-card>
    <app-card-header>
      <app-card-title>
        <div class="header-row">
          <div class="title-section mr-auto">
            <h2 class="m-0 white-space-nowrap">{{ (isEditMode() ? 'checkpoint.form.title.edit' :
              'checkpoint.form.title.new') |
              translate }}</h2>
          </div>

          <div class="form-actions">

            @if (isEditMode() && canDelete()) {
            <app-button class="" variant="outlined" color="danger" type="button" (click)="openDeleteModal()"
              [disabled]="saving() || deleting()" data-testid="delete-button">
              <app-icon name="trash-2" />
              {{ 'checkpoint.delete.button' | translate }}
            </app-button>
            }
            <app-button variant="outlined" type="button" (click)="onCancel()" [disabled]="saving()"
              data-testid="cancel-button">
              <app-icon name="x" />
              {{ 'button.cancel' | translate }}
            </app-button>
            <app-button variant="outlined" type="button" (click)="onSave()" [disabled]="saveDisabled()"
              data-testid="save-button">
              <app-icon name="save" />
              {{ 'checkpoint.form.save' | translate }}
            </app-button>
            <app-button variant="filled" type="button" (click)="onSaveAndClose()" [disabled]="saveDisabled()"
              data-testid="save-and-close-button">
              @if (saving()) {
              <app-spinner [diameter]="20"></app-spinner>
              } @else {
              <app-icon name="save" />
              }
              {{ 'checkpoint.form.saveAndClose' | translate }}
            </app-button>
          </div>
        </div>
      </app-card-title>
    </app-card-header>

    <app-card-content>
      @if (loading()) {
      <div class="loading-container">
        <app-spinner></app-spinner>
        <p>{{ 'checkpoint.form.loading' | translate }}</p>
      </div>
      } @else if (error()) {
      <div class="error-message">
        <app-icon name="alert-circle" [color]="'var(--fg-danger)'" />
        <p>{{ error() }}</p>
      </div>
      } @else {
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <formly-form [form]="form" [fields]="fields()" [model]="model"></formly-form>

        <!-- Selected Forms Section -->
        <div class="forms-section">
          <div class="forms-header">
            <h3>{{ 'checkpoint.form.forms.title' | translate }}</h3>
            <app-button variant="outlined" type="button" (click)="selectForms()" data-testid="select-forms-button">
              <app-icon name="plus" />
              {{ 'checkpoint.form.selectForms' | translate }}
            </app-button>
          </div>

          @if (selectedForms().length === 0) {
          <div class="empty-state">
            <p class="empty-message">{{ 'checkpoint.form.forms.empty' | translate }}test</p>
          </div>
          } @else {
          <div class="selected-forms-list">
            @for (form of selectedForms(); track form.id) {
            <div class="form-card" [attr.data-testid]="'selected-form-' + form.id">
              <div class="form-info">
                <div class="form-name">{{ form.name }}</div>
              </div>
              <div class="form-actions">
                <app-button variant="icon" type="button" (click)="showFormView(form)" data-testid="view-form-button"
                  [tippy]="'checkpoint.form.view.viewForm' | translate">
                  <app-icon name="eye" />
                </app-button>
                <app-button variant="icon" type="button" (click)="removeForm(form.id!)"
                  [tippy]="'checkpoint.form.forms.remove' | translate" [attr.data-testid]="'remove-form-' + form.id">
                  <app-icon name="list-x" />
                </app-button>
              </div>
            </div>
            }
          </div>
          <div class="forms-count">
            {{ 'checkpoint.form.forms.count' | translate: { count: selectedForms().length.toString() } }}
          </div>
          }
        </div>

        <!-- Copies / Instances Section (only in edit mode) -->
        @if (isEditMode()) {
        <div class="instances-section">
          <div class="instances-header">
            <h3>{{ 'checkpoint.instances.title' | translate }}</h3>
            <div class="instances-header-actions">
              @if (activeInstanceCount() > 0) {
              <app-button variant="text" type="button" (click)="toggleShowDeleted()"
                data-testid="toggle-deleted-button">
                {{ (showDeleted() ? 'checkpoint.instances.hideDeleted' : 'checkpoint.instances.showDeleted') | translate
                }}
              </app-button>
              }
              <app-button variant="outlined" type="button" (click)="openAddCopies()" data-testid="add-copies-button">
                <app-icon name="plus" />
                {{ (activeInstanceCount() > 0 ? 'checkpoint.instances.addMore' : 'checkpoint.instances.addCopies') |
                translate }}
              </app-button>
            </div>
          </div>

          @if (showAddCopies()) {
          <div class="add-copies-form" data-testid="add-copies-form">
            <div class="add-copies-fields">
              <label class="add-copies-field">
                <span>{{ 'checkpoint.instances.quantity' | translate }}</span>
                <input type="number" min="1" max="500" [ngModel]="addCopiesQuantity()"
                  (ngModelChange)="addCopiesQuantity.set($event)" [ngModelOptions]="{standalone: true}"
                  data-testid="copies-quantity" />
              </label>
              <label class="add-copies-field">
                <span>{{ 'checkpoint.instances.startingSerial' | translate }}</span>
                <input type="number" min="1" [ngModel]="addCopiesStartSerial()"
                  (ngModelChange)="addCopiesStartSerial.set($event)" [ngModelOptions]="{standalone: true}"
                  data-testid="copies-start-serial" />
              </label>
              <label class="add-copies-field">
                <span>{{ 'checkpoint.instances.prefix' | translate }}</span>
                <input type="text" [ngModel]="addCopiesPrefix()" (ngModelChange)="addCopiesPrefix.set($event)"
                  [ngModelOptions]="{standalone: true}" data-testid="copies-prefix" />
              </label>
            </div>
            <div class="add-copies-preview">
              {{ 'checkpoint.instances.preview' | translate: { serials: previewSerials() } }}
            </div>
            <div class="add-copies-actions">
              <app-button variant="text" type="button" (click)="cancelAddCopies()">
                {{ 'checkpoint.instances.cancel' | translate }}
              </app-button>
              <app-button variant="filled" type="button" (click)="confirmAddCopies()" [disabled]="addingCopies()"
                data-testid="confirm-add-copies">
                @if (addingCopies()) {
                <app-spinner [diameter]="18"></app-spinner>
                }
                {{ 'checkpoint.instances.create' | translate }}
              </app-button>
            </div>
          </div>
          }

          @if (displayedInstances().length === 0 && !showAddCopies()) {
          <div class="empty-state">
            <p class="empty-message">{{ 'checkpoint.instances.empty' | translate }}</p>
          </div>
          } @else if (displayedInstances().length > 0) {
          <div class="instances-list" data-testid="instances-list">
            @for (instance of displayedInstances(); track instance.id) {
            <div class="instance-row" [class.instance-deleted]="instance.is_deleted"
              [attr.data-testid]="'instance-' + instance.serial_number">
              <div class="instance-serial">
                @if (editingInstanceId() === instance.id) {
                <input type="text" class="serial-edit-input" [ngModel]="editingSerialValue()"
                  (ngModelChange)="editingSerialValue.set($event)" [ngModelOptions]="{standalone: true}"
                  (keyup.enter)="saveEditSerial(instance)" (keyup.escape)="cancelEditSerial()"
                  data-testid="edit-serial-input" />
                @if (serialValidationError()) {
                <span class="serial-error">{{ serialValidationError() }}</span>
                }
                } @else {
                <span class="serial-label">#{{ instance.serial_number }}</span>
                @if (instance.is_deleted) {
                <span class="deleted-badge">{{ 'checkpoint.instances.deleted' | translate }}</span>
                }
                }
              </div>
              <div class="instance-actions">
                @if (editingInstanceId() === instance.id) {
                <app-button variant="icon" type="button" (click)="saveEditSerial(instance)" data-testid="save-serial">
                  <app-icon name="check" />
                </app-button>
                <app-button variant="icon" type="button" (click)="cancelEditSerial()" data-testid="cancel-serial">
                  <app-icon name="x" />
                </app-button>
                } @else if (instance.is_deleted) {
                <app-button variant="text" type="button" (click)="restoreInstance(instance)"
                  data-testid="restore-instance">
                  {{ 'checkpoint.instances.restore' | translate }}
                </app-button>
                } @else {
                <app-button variant="icon" type="button" (click)="showInstanceView(instance)"
                  [tippy]="'checkpoint.instances.viewForms' | translate" [disabled]="selectedForms().length === 0"
                  [attr.data-testid]="'view-instance-' + instance.serial_number">
                  <app-icon name="eye" />
                </app-button>
                <app-button variant="icon" type="button" (click)="startEditSerial(instance)"
                  [tippy]="'checkpoint.instances.editSerial' | translate" data-testid="edit-serial">
                  <app-icon name="pencil" />
                </app-button>
                <app-button variant="icon" type="button" (click)="deleteInstance(instance)"
                  [tippy]="'checkpoint.instances.delete' | translate" data-testid="delete-instance">
                  <app-icon name="trash-2" />
                </app-button>
                }
              </div>
            </div>
            }
          </div>
          @if (activeInstanceCount() > 0) {
          <div class="instances-count">
            {{ 'checkpoint.instances.count' | translate: { count: activeInstanceCount().toString() } }}
          </div>
          }
          }
        </div>
        }
      </form>
      }
    </app-card-content>
  </app-card>

  @if (showDeleteModal()) {
  <app-modal-dialog (backdropClick)="cancelDelete()">
    <div header>
      <div class="modal-title-row">
        <div class="heading-md">{{ 'checkpoint.delete.title' | translate }}</div>
        <app-button variant="icon" (click)="cancelDelete()">
          <app-icon name="x" />
        </app-button>
      </div>
    </div>
    <div content>
      <p>{{ 'checkpoint.delete.confirm' | translate:{ name: model.name } }}</p>
      <p>{{ 'checkpoint.delete.warning' | translate }}</p>
    </div>
    <div footer>
      <app-button variant="text" (click)="cancelDelete()">
        {{ 'button.cancel' | translate }}
      </app-button>
      <app-button color="danger" (click)="confirmDelete()">
        {{ 'checkpoint.delete.button' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
  }
</div>